import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from './hooks/useAuth';
import { useSubscription } from './contexts/SubscriptionContext';
import SubscriptionPaywall from './components/subscription/SubscriptionPaywall';
import { EASI_DESIGN } from './theme/design-tokens';
import { PageWrapper } from './components/ui/animations';
import BottomDock from './components/BottomDock';
import AgreementGuard from './components/agreements/AgreementGuard';
import {
  LiteracyCurriculumRoute,
  LiteracyProgressRoute,
  LiteracyDashboardRoute,
  LiteracyLessonRoute,
  LiteracyExploreCurriculumRoute,
  LiteracyExploreLessonRoute,
} from './components/literacy/LiteracyRoutes';
import {
  TherapySessionRoute,
  TherapyInboxRoute,
} from './components/therapy/TherapyRoutes';

const d = EASI_DESIGN;

// Lazy load components
const Login = React.lazy(() => import('./components/auth/Login'));
const EASISystem = React.lazy(() => import('./components/easi-system'));
const Assessment = React.lazy(() => import('./components/Assessment'));
const LanguageSampleWorkflow = React.lazy(() => import('./components/language-sample-workflow'));
const SubscriptionPage = React.lazy(() => import('./components/subscription/SubscriptionPage'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const ReportGeneratorWizard = React.lazy(() => import('./components/report-generator/ReportGeneratorWizard'));
const ReportDashboard = React.lazy(() => import('./components/report-generator/ReportDashboard'));
const TermsOfServicePage = React.lazy(() => import('./components/agreements/TermsOfServicePage'));
const BAASelectionPage = React.lazy(() => import('./components/agreements/BAASelectionPage'));
const BAAAcceptancePage = React.lazy(() => import('./components/agreements/BAAAcceptancePage'));
const ActivateAccount = React.lazy(() => import('./components/auth/ActivateAccount'));

const LOADING_MESSAGES = [
  'Preparing your caseload...',
  'Connecting to secure services...',
  'Loading patient records...',
  'Setting up your workspace...',
];

const LOADING_MSG_INTERVAL = 3000;

/**
 * Premium branded loading screen — first impression on every session.
 * White background, full-size logo, green waveform bars, crossfading status text.
 */
function EasiLoadingScreen() {
  const [msgIdx, setMsgIdx] = useState(0);
  const [msgFade, setMsgFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setMsgFade(false);
      setTimeout(() => {
        setMsgIdx((prev) => (prev + 1) % LOADING_MESSAGES.length);
        setMsgFade(true);
      }, 260);
    }, LOADING_MSG_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#FFFFFF' }}
    >
      <style>{`
        @keyframes easiWaveScale {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>

      {/* Logo — full size, crisp */}
      <Box
        component="img"
        src="/easi-logo.png"
        alt="EASI"
        sx={{
          width: 96,
          height: 96,
          mb: 3,
          display: 'block',
        }}
      />

      {/* Green waveform bars */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 32, marginBottom: 24 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 4,
              height: 28,
              borderRadius: 2,
              backgroundColor: '#10B981',
              transformOrigin: 'center',
              animation: `easiWaveScale 1.4s ease-in-out ${i * 0.12}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Crossfading status text */}
      <Typography
        variant="body2"
        sx={{
          color: '#64748B',
          fontWeight: 500,
          fontSize: 14,
          opacity: msgFade ? 1 : 0,
          transition: 'opacity 260ms ease',
          minHeight: 22,
          userSelect: 'none',
        }}
      >
        {LOADING_MESSAGES[msgIdx]}
      </Typography>
    </Box>
  );
}

/**
 * Protected Route wrapper — shows branded loading while auth initializes
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <EasiLoadingScreen />;
  }

  if (!isAuthenticated) {
    const intended = location.pathname + location.search;
    if (intended !== '/') {
      sessionStorage.setItem('easi_redirect_after_login', intended);
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin Route guard — redirects non-admin users to caseload.
 * Admin role is determined from Cognito groups in the ID token.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  try {
    const token = localStorage.getItem('easi_id_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const groups = payload['cognito:groups'] || [];
      const isAdmin = groups.some((g: string) => g.toLowerCase().includes('admin'));
      if (isAdmin) return <>{children}</>;
    }
  } catch {
    // Token parse failed
  }
  return <Navigate to="/" replace />;
}

/**
 * Public Route wrapper — redirects to dashboard if already authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <EasiLoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Wrapper for LanguageSampleWorkflow with route params
 */
function LanguageSamplePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  return (
    <LanguageSampleWorkflow
      patientName="Patient"
      patientId={patientId || 'new'}
      clientAgeMonths={48}
      onComplete={() => {
        navigate(`/assessment/${patientId}`);
      }}
      onBack={() => navigate(`/assessment/${patientId}`)}
    />
  );
}

/**
 * Main App Component
 *
 * EASI 2.0 - HIPAA-Compliant Clinical Speech Assessment Platform
 */
function App() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { hasSubscription, isLoading: subLoading, subscription, error: subError } = useSubscription();
  const navigate = useNavigate();
  const hasHandledPostLoginRedirect = useRef(false);

  // Admin users bypass subscription gate
  const isAdmin = useMemo(() => {
    if (!isAuthenticated) return false;
    try {
      const token = localStorage.getItem('easi_id_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        return groups.some((g: string) => g.toLowerCase().includes('admin'));
      }
    } catch {}
    return false;
  }, [isAuthenticated]);

  // After SSO login (Google/Apple), redirect to the originally requested page.
  // Email/password login handles this in Login.tsx directly.
  // All users (including admins) land on the caseload. Admins navigate to /admin manually.
  useEffect(() => {
    if (isAuthenticated && !hasHandledPostLoginRedirect.current) {
      hasHandledPostLoginRedirect.current = true;

      // Check both sessionStorage (set by ProtectedRoute) and localStorage
      // (set by session timeout / auth failure before hard redirect)
      const redirectPath = sessionStorage.getItem('easi_redirect_after_login')
        || localStorage.getItem('easi_redirect_after_login');
      if (redirectPath) {
        sessionStorage.removeItem('easi_redirect_after_login');
        localStorage.removeItem('easi_redirect_after_login');
        navigate(redirectPath, { replace: true });
      }
      // No admin redirect — everyone lands on caseload
    } else if (!isAuthenticated) {
      hasHandledPostLoginRedirect.current = false;
    }
  }, [isAuthenticated, navigate, location.pathname]);

  // ── Subscription paywall ──────────────────────────────────────────────
  // EASI has NO free tier. All access requires an active NSS subscription.
  // This gate blocks ALL routes — no URL navigation can bypass it.
  if (isAuthenticated && location.pathname !== '/login') {
    // Subscription data still loading — show branded loading screen.
    // CRITICAL: Use && not || so tab-return re-fetches (subLoading=true but
    // subscription already populated) do NOT unmount the component tree.
    // Only the initial load (subscription===null) triggers the loading screen.
    if (subLoading && subscription === null && !subError) {
      return <EasiLoadingScreen />;
    }
    // No subscription and not admin → full-screen paywall
    if (!hasSubscription && !isAdmin) {
      return <SubscriptionPaywall />;
    }
  }

  return (
    <React.Suspense fallback={<EasiLoadingScreen />}>
      {isAuthenticated &&
        location.pathname !== '/login' &&
        location.pathname !== '/terms-of-service' &&
        location.pathname !== '/baa-selection' &&
        location.pathname !== '/baa' &&
        <BottomDock />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <PageWrapper>
                  <Login />
                </PageWrapper>
              </PublicRoute>
            }
          />

          {/* Magic-link activation — public, token-based. No auth guard:
              a logged-in user following a link from their welcome email
              still needs to be able to activate. */}
          <Route
            path="/activate"
            element={
              <PageWrapper>
                <ActivateAccount />
              </PageWrapper>
            }
          />

          {/* Agreement routes — protected but NOT agreement-guarded */}
          <Route
            path="/terms-of-service"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <TermsOfServicePage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/baa-selection"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <BAASelectionPage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/baa"
            element={
              <ProtectedRoute>
                <PageWrapper>
                  <BAAAcceptancePage />
                </PageWrapper>
              </ProtectedRoute>
            }
          />

          {/* Protected routes — wrapped with AgreementGuard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <EASISystem />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <Assessment />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment/:patientId/language-sample"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LanguageSamplePage />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/subscription"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <SubscriptionPage />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/report-generator"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportDashboard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-generator/new"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportGeneratorWizard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report-generator/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <ReportGeneratorWizard />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AgreementGuard>
                    <PageWrapper>
                      <AdminDashboard />
                    </PageWrapper>
                  </AgreementGuard>
                </AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Literacy routes (explore mode — no patient required) */}
          <Route
            path="/literacy"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyExploreCurriculumRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/literacy/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyExploreLessonRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Literacy routes (patient mode — progress tracked) */}
          <Route
            path="/patients/:patientId/literacy"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyDashboardRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/curriculum"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyCurriculumRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/progress"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyProgressRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/:patientId/literacy/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <LiteracyLessonRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Therapy Notes routes */}
          <Route
            path="/therapy/inbox"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <TherapyInboxRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/therapy/:patientId"
            element={
              <ProtectedRoute>
                <AgreementGuard>
                  <PageWrapper>
                    <TherapySessionRoute />
                  </PageWrapper>
                </AgreementGuard>
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </React.Suspense>
  );
}

export default App;
